import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from 'hooks/useDebounce';
import { useFileManagerContext } from 'modules/FileManagerV2/context/FileManagerContext';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import GlobalTooltip from 'components/GlobalTooltip';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { globalSearchEvent } from 'services/generalServices';
import axios from 'axios';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';

function SearchFiles() {
  const {
    query,
    setQuery,
    isCaseFileManager,
    isLeadFileManager,
    caseDetails,
    setOverrideFiles,
    handleGetFiles,
    pageLimit,
    setIsContentSearchActive,
    isContentSearchActive,
  } = useFileManagerContext();

  const { checkSubPermission } = useUserDetailsContext();
  const [loading, setLoading] = useState(false);
  const { addToast } = useToast();

  const cancelTokenSource = useRef(null);
  const hasSearchResults = useRef(false);
  const isContentSearchLoadingRef = useRef(false);

  const debouncedQuery = useDebounce(query, 400);

  const handleToggleContentSearch = async () => {
    try {
      if (cancelTokenSource.current) {
        cancelTokenSource.current.cancel();
      }

      const nextActive = !isContentSearchActive;
      setIsContentSearchActive(nextActive);

      if (!nextActive) {
        setOverrideFiles(null);
        hasSearchResults.current = false;
        setLoading(true);
        await Promise.resolve(handleGetFiles(pageLimit));
        return;
      }
      hasSearchResults.current = false;
    } catch (error) {
      if (!axios.isCancel(error)) {
        addToast(false, toastConstant.toasterType.ERROR, 'Content Search', 'Failed to search content');
      }
    } finally {
      setLoading(false);
    }
  };

  const canShowContentButton = useMemo(
    () => checkSubPermission('full_text_search') && (isCaseFileManager || isLeadFileManager),
    [checkSubPermission, isCaseFileManager, isLeadFileManager]
  );

  const onChangeSearchFiles = (value) => setQuery(value);

  const mapSearchResultToFile = (bucket) => {
    const src = bucket?.top_hit?.hits?.hits?.[0]?._source || {};

    return {
      ...src,
      display_name: decodeURIComponent(src?.file_name || src?.display_name || ''),
      entry_type: 'file',
      object_id: src?.document_id,
      file_path: src?.object_key,
      case_id: src?.case_id,
      case_name: src?.case_name,
      size: src?.size,
      time: src?.time,
      res_type: 'file_system_entry',
    };
  };

  const handleEmptyQuery = async () => {
    cancelOngoingRequest();

    if (!hasSearchResults.current) {
      setOverrideFiles(null);
      return;
    }

    try {
      isContentSearchLoadingRef.current = true;
      setLoading(true);

      await handleGetFiles(pageLimit);

      setOverrideFiles(null);
      hasSearchResults.current = false;
    } catch {
      setOverrideFiles(null);
    } finally {
      isContentSearchLoadingRef.current = false;
      setLoading(false);
    }
  };

  const cancelOngoingRequest = () => {
    if (cancelTokenSource.current) {
      cancelTokenSource.current.cancel();
    }
  };

  const performContentSearch = async () => {
    try {
      cancelOngoingRequest();

      cancelTokenSource.current = axios.CancelToken.source();
      setLoading(true);

      const response = await globalSearchEvent(
        debouncedQuery,
        true,
        true,
        true,
        { cancelToken: cancelTokenSource.current.token },
        '',
        1,
        pageLimit || 50,
        '',
        '',
        '',
        '',
        '',
        '',
        caseDetails?.case_id || ''
      );

      const buckets = response?.data?.results?.documents?.buckets ?? response?.data?.documents?.buckets ?? [];

      const mappedFiles = buckets.map(mapSearchResultToFile);

      setOverrideFiles(mappedFiles);
      hasSearchResults.current = true;
    } catch (error) {
      if (!axios.isCancel(error)) {
        addToast(false, toastConstant.toasterType.ERROR, 'Content Search', 'Failed to search content');
        hasSearchResults.current = false;
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isContentSearchActive) {
      hasSearchResults.current = false;
      return;
    }
    if (!debouncedQuery?.trim()) {
      handleEmptyQuery();
      return;
    }
    performContentSearch();
    return () => cancelOngoingRequest();
  }, [isContentSearchActive, debouncedQuery, pageLimit, caseDetails?.case_id]);

  return (
    <div className="autowidth d-flex align-items-center">
      <div className="p-input-icon-left input-shadow w-100 position-relative">
        <i className="icon-search F-size16" />

        <TextSnippetWrapper
          type="text"
          className=" w-100 input-search"
          value={query || ''}
          onChange={(e) => onChangeSearchFiles(e.target.value)}
          placeholder="Search"
          autoComplete="off"
          style={{ paddingRight: canShowContentButton ? '2.5rem' : undefined }}
        />

        {canShowContentButton && (
          <span
            role="button"
            className="d-inline-flex align-items-center"
            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 2, pointerEvents: 'auto' }}
            onClick={handleToggleContentSearch}
          >
            <GlobalTooltip
              tooltip="Search Content Data"
              className="ms-2"
              style={isContentSearchActive ? { background: 'var(--primary-main)', color: '#fff', borderRadius: 6, padding: '4px' } : {}}
            >
              {loading ? (
                <i className="pi pi-spin pi-spinner fa-lg" />
              ) : (
                <i
                  className={`icon-files fa-lg ${isContentSearchActive ? 'highlight' : ''}`}
                  style={isContentSearchActive ? { color: '#fff' } : {}}
                />
              )}
            </GlobalTooltip>
          </span>
        )}
      </div>
    </div>
  );
}

export default SearchFiles;
